// ============================================================
// ClientDocumentUploadModal.tsx
// "Select-then-Save" upload UX for client-detail mode. The modal
// owns the upload logic directly — it does NOT embed the shared
// FileUpload component (which fires-on-pick and is correct for
// Case Detail, where there's no scope to choose). Here the user:
//   1. Picks a scope via the top <select>:
//        - "Just to this client (not tied to any case)" — client-scoped
//        - "Case <short-id> · Accident: <date>" — case-scoped
//   2. Picks a file via click or drag-and-drop on the drop zone.
//   3. Reviews the queued file (filename + size) and the scope.
//   4. Clicks Save to actually fire the upload, OR changes their
//      mind on scope, OR clicks Cancel to discard.
//
// Why deferred? The previous fire-on-pick design (Phase 2c/2d) let
// users upload to the wrong scope by accident — they'd pick a file
// while the default "Just to this client" was still selected,
// thinking they were uploading to a case. Save-on-click eliminates
// that whole class of mistake: nothing happens until the user
// explicitly commits.
//
// Modal mechanics (overlay + dialog refs, onMouseDown backdrop
// close with target-id guard, window-level Escape close + Tab focus
// trap, rAF-deferred auto-focus on the scope select, restore-focus
// on close) are ported from ClientPickerModal — same shape across
// all Casevo modals.
//
// Upload flow (handleSave):
//   - requestUploadUrlFlatApi({ caseId?, clientId?, filename,
//     mimeType, sizeBytes }) → presigned PUT URL + fileId. The
//     flat endpoint accepts either scope; backend XOR-validates.
//   - fetch PUT directly to R2 — plain fetch, NOT axios. The
//     Bearer-token request interceptor would invalidate the
//     presigned signature.
//   - On success: 2-second "Uploaded ✓" display, then reset
//     selectedFile to null. Modal STAYS OPEN — user dismisses
//     manually. Lets them upload another doc to the same scope
//     without re-opening.
//   - On failure: orphan cleanup via deleteFileFlatApi (the row
//     was atomically created at upload-url request time; if R2
//     PUT failed we wipe it). Error surfaces inline below the
//     drop zone; modal stays open for retry.
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import { formatDateUK } from '../../utils/caseHelpers'
import { requestUploadUrlFlatApi, deleteFileFlatApi } from '../../api/casevoApi'
import { caseShortId } from '../../utils/caseName'


// Matches the backend storage cap (1 GiB) on POST /upload-url — large
// evidence media is storable. Held at module scope so it's visible to the
// validate helper below without dragging it through props.
const MAX_BYTES = 1024 * 1024 * 1024

// Same validation rules the backend enforces, so the user sees the
// same error inline instead of waiting for a 400.
const validate = (file: File): string | null => {
  if (!file.name.trim()) return 'Filename cannot be empty'
  if (file.size <= 0 || file.size > MAX_BYTES) {
    return 'File too large (max 1 GB)'
  }
  return null
}

// Compact byte → human-readable formatter used for the queued-
// file display. Bytes for <1 KiB, otherwise one decimal place
// for KB / MB. Matches FileList.formatBytes' shape (this and
// DocumentRow.formatFileSize live in three different places
// because they're each on a different rendering hot path; a
// shared formatters module is a future tidy).
const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

// Inline-defined here because the type is only consumed by this
// modal + ClientSearchView (where it's also inline-defined). Future
// cleanup could lift it to a shared types file.
interface ClientCaseRow {
  id: string
  accidentDate: string | null
  closeDate: string | null
  setupFinalised: boolean
  updatedAt: string
  // THE case reference (SURNAME-YEAR-ID), precomputed by the parent (caseRef).
  ref?: string
}

interface ClientDocumentUploadModalProps {
  isOpen: boolean
  onClose: () => void
  clientId: string
  clientDisplayName: string
  cases: ClientCaseRow[]
  onUploadComplete: () => void
}

const ClientDocumentUploadModal: React.FC<ClientDocumentUploadModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientDisplayName,
  cases,
  onUploadComplete,
}) => {
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const scopeRef = useRef<HTMLSelectElement | null>(null)
  // Hidden <input type="file"> ref — opened programmatically when
  // the drop zone is clicked. Same trick FileUpload uses.
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  // Holds the 2-second success-display reset timer so it can be
  // cancelled if the user clicks Cancel / Escape during the fade.
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Scope state. 'client' for client-scoped upload; 'case:<id>' for
  // case-scoped. Single string avoids two-variable juggling
  // (scope-type + scope-id) and serialises cleanly into the <select>
  // value/option pattern.
  const [scope, setScope] = useState<string>('client')

  // Upload-flow state. selectedFile is the queued file (set on
  // pick, cleared on Save-success or Cancel). validationError is
  // the client-side check result (filename / size). errorMessage
  // is the upload-failure message. mode drives the drop-zone
  // visual state. isDragging shifts the border colour during a
  // drag-over.
  type UploadMode = 'idle' | 'uploading' | 'success' | 'error'
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [mode, setMode] = useState<UploadMode>('idle')
  const [isDragging, setIsDragging] = useState(false)

  // Reset scope + ALL upload-flow state on open. Modal can be re-
  // opened for the same client multiple times; user starts fresh
  // each time. Also captures the previously-focused element for
  // restore-on-close, and cancels any pending success-display timer
  // (in case the modal was closed mid-fade and is now re-opening).
  useEffect(() => {
    if (isOpen) {
      setScope('client')
      setSelectedFile(null)
      setValidationError(null)
      setErrorMessage(null)
      setMode('idle')
      setIsDragging(false)
      if (successTimerRef.current !== null) {
        clearTimeout(successTimerRef.current)
        successTimerRef.current = null
      }
      previouslyFocusedRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null
    }
    return () => {
      if (isOpen) {
        previouslyFocusedRef.current?.focus?.()
      }
    }
  }, [isOpen])

  // Timer-cleanup safety net (Activity/Documents merge, increment 3).
  // The success setTimeout now calls onClose() rather than resetting
  // to idle in place. Every KNOWN close path clears the timer:
  //   - Cancel button   → handleCancel → clearTimeout
  //   - Escape key      → onClose direct — cleared by the isOpen
  //                       effect's belt-and-braces on next open
  //   - Backdrop click  → onClose direct — same as above
  //   - Re-open         → isOpen effect L138+ clears the timer
  // But a TRUE UNMOUNT (parent removes the modal from the tree
  // without going through an isOpen: true → false transition first)
  // is only covered by this effect: an empty-deps useEffect whose
  // cleanup fires ONLY on unmount. Prevents the exact stale-timer
  // bug family we've fixed before — a setTimeout firing on a dead
  // component and calling onClose() (or worse, setState) on nothing.
  useEffect(() => {
    return () => {
      if (successTimerRef.current !== null) {
        clearTimeout(successTimerRef.current)
        successTimerRef.current = null
      }
    }
  }, [])

  // Auto-focus the scope select on open. rAF-deferred so the DOM is
  // committed before we ask for focus — otherwise scopeRef.current
  // is null on the first try.
  useEffect(() => {
    if (!isOpen) return
    const id = requestAnimationFrame(() => {
      scopeRef.current?.focus()
    })
    return () => cancelAnimationFrame(id)
  }, [isOpen])

  // Keyboard handling — Escape close + Tab focus trap. Single window-
  // level listener; freshest dialog content is read via dialogRef at
  // keydown time so changes to the focusable set (e.g. the Save
  // button enabling once a file is queued) are picked up without re-
  // attaching the listener.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => el.offsetParent !== null)
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  // Backdrop close — onMouseDown (not onClick) so a click that
  // starts inside the dialog and drags onto the backdrop doesn't
  // close. Target-id guard ensures clicks bubbling from inside the
  // dialog don't trigger close either.
  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose()
  }

  // ── DROP-ZONE HANDLERS ───────────────────────────────────────
  // openFilePicker forwards a synthetic click to the hidden native
  // <input type="file">. Disabled mid-upload to prevent a second
  // pick from clobbering the in-flight state.
  const openFilePicker = (): void => {
    if (mode === 'uploading') return
    fileInputRef.current?.click()
  }
  // handleFileSelected validates + stages the file. Runs the same
  // backend-mirroring rules (filename / size) FileUpload uses, so
  // the user sees the same error inline rather than waiting for a
  // 400 from the upload-url request.
  const handleFileSelected = (file: File): void => {
    setValidationError(null)
    setErrorMessage(null)
    const err = validate(file)
    if (err) {
      setValidationError(err)
      return
    }
    setSelectedFile(file)
    // If the user picked a new file after an error or success, reset
    // mode so the drop zone shows the queued-file view instead of a
    // stale "Uploaded ✓" or error.
    setMode('idle')
  }
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    // Reset the input value so picking the SAME file twice still
    // fires onChange (browsers suppress change when value matches).
    e.target.value = ''
    if (file) handleFileSelected(file)
  }
  const onDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    if (mode === 'uploading') return
    // preventDefault is what tells the browser "this element accepts
    // drops" — without it, onDrop never fires.
    e.preventDefault()
    setIsDragging(true)
  }
  const onDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDragging(false)
  }
  const onDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDragging(false)
    if (mode === 'uploading') return
    const file = e.dataTransfer.files?.[0]
    if (file) handleFileSelected(file)
  }

  // ── SAVE (THE ACTUAL UPLOAD) ─────────────────────────────────
  // Runs ONLY on Save click. Builds the request body from the
  // current scope + selectedFile, fires requestUploadUrlFlatApi,
  // PUTs to R2 via plain fetch (axios's interceptor would invalidate
  // the presigned signature), and on success refreshes the parent's
  // list + shows a brief "Uploaded ✓" acknowledgement before
  // auto-closing the modal.
  //
  // AUTO-CLOSE (Activity/Documents merge, increment 3): the pre-
  // merge behaviour left the modal open on success so the solicitor
  // could see the queued upload against a visible Documents list.
  // Now that documents land in the Activity timeline BEHIND the
  // modal, the "stays open" state reads as stuck. New behaviour:
  // 1200ms success acknowledgement, then onClose(). The previous
  // reset-to-idle setTimeout is REPLACED (not additive) — a
  // reset would race the unmount for no user-visible gain, and
  // reopen freshness is handled by the isOpen-effect above.
  // Multi-file uploads now cost one extra click of "+ Upload
  // document" per file; accepted trade for the fixed close UX.
  const handleSave = async (): Promise<void> => {
    if (!selectedFile || mode === 'uploading') return
    setErrorMessage(null)
    setMode('uploading')
    let fileId: string | null = null
    try {
      // Map scope state → API param. caseId-mode passes caseId only;
      // client-mode passes clientId only. Backend XOR-validates so a
      // stale both/neither would 400.
      const caseId = scope.startsWith('case:') ? scope.replace(/^case:/, '') : undefined
      const targetClientId = scope === 'client' ? clientId : undefined
      const provisioned = await requestUploadUrlFlatApi({
        caseId,
        clientId: targetClientId,
        filename: selectedFile.name,
        mimeType: selectedFile.type,
        sizeBytes: selectedFile.size,
      })
      fileId = provisioned.fileId
      // Plain fetch, NOT axios — Authorization from the axios
      // interceptor would invalidate the presigned R2 signature.
      // ONLY Content-Type is sent.
      const r2Response = await fetch(provisioned.uploadUrl, {
        method: 'PUT',
        // The type the server resolved (it fills in .msg / .eml the browser left blank).
        headers: { 'Content-Type': provisioned.mimeType ?? selectedFile.type },
        body: selectedFile,
      })
      if (!r2Response.ok) {
        throw new Error(`Upload failed (status ${r2Response.status})`)
      }
      setMode('success')
      // Tell the parent to refresh now so the new doc appears in the
      // background timeline. Kept BEFORE the close timer so the
      // parent's fetch is in flight during the acknowledgement window
      // — by the time the modal fades on close, the timeline usually
      // shows the new row.
      onUploadComplete()
      // Auto-close after a brief "Uploaded ✓" acknowledgement.
      // 1200ms is long enough to register the tick without dragging.
      // The setTimeout id lives on successTimerRef so every close
      // path (handleCancel, Escape, backdrop, unmount) can cancel
      // it before it fires.
      successTimerRef.current = setTimeout(() => {
        successTimerRef.current = null
        onClose()
      }, 1200)
    } catch (uploadErr) {
      // Orphan cleanup — if the backend committed a FileAttachment row
      // before the R2 PUT failed, wipe it via the flat delete endpoint.
      // Swallow cleanup errors; we already have the real upload error.
      if (fileId !== null) {
        try {
          await deleteFileFlatApi(fileId)
        } catch {
          /* intentional swallow — orphan cleanup is best-effort */
        }
      }
      const msg = uploadErr instanceof Error ? uploadErr.message : 'Upload failed. Please try again.'
      setErrorMessage(msg)
      setMode('error')
    }
  }

  // ── CANCEL ───────────────────────────────────────────────────
  // Discards the queued file + any pending success-display timer,
  // then closes the modal. Distinct from onClose (which just hides
  // the modal) because we want the local state cleared so the next
  // open starts fresh — the open-effect resets state too but this
  // is the synchronous version that fires before the close completes.
  const handleCancel = (): void => {
    if (successTimerRef.current !== null) {
      clearTimeout(successTimerRef.current)
      successTimerRef.current = null
    }
    setSelectedFile(null)
    setValidationError(null)
    setErrorMessage(null)
    setMode('idle')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      ref={overlayRef}
      onMouseDown={handleBackdropMouseDown}
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4 py-6"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-modal-title"
        className="bg-casevo-surface rounded-lg border border-casevo-border w-full max-w-lg max-h-[90vh] flex flex-col"
      >
        {/* Header — fixed; flex-shrink-0 keeps it pinned when the
            body scrolls under it. */}
        <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-casevo-border flex-shrink-0">
          <h3
            id="upload-modal-title"
            className="text-sm font-medium text-casevo-forest uppercase tracking-widest"
          >
            Upload document for {clientDisplayName}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-casevo-mid hover:text-casevo-forest text-lg leading-none px-2 -mr-2"
          >
            ✕
          </button>
        </div>

        {/* Scrollable body — wraps the scope select + drop zone +
            footer buttons. Nothing to scroll on a fresh open, but
            if the scope picker plus an error message push past
            90vh on a small viewport, the body scrolls under the
            fixed header. */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-casevo-mid mb-1 block">
                Upload scope
              </span>
              <select
                ref={scopeRef}
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className="w-full border border-casevo-border rounded px-3 py-2 text-sm bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid"
              >
                <option value="client">Just to this client (not tied to any case)</option>
                {cases.map((c) => (
                  <option key={c.id} value={`case:${c.id}`}>
                    {`Case ${c.ref ?? caseShortId(c.id)}${c.accidentDate ? ` · Accident: ${formatDateUK(c.accidentDate)}` : ''}`}
                  </option>
                ))}
              </select>
            </label>

            {/* Hidden native file input — opened programmatically
                when the drop zone is clicked. Same trick as FileUpload. */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={onInputChange}
            />
            {/* Drop zone — click to open the OS file picker OR drag-
                and-drop a file onto it. Four visual states:
                  • idle (no file picked)  — invitation copy
                  • queued (file selected) — filename + size + "Click
                    to choose a different file" hint
                  • uploading              — "Uploading {filename}…"
                  • success                — "Uploaded ✓" (2 seconds)
                Border colour shifts to casevo-mid during drag-over
                and on hover; opacity dims during upload to signal
                non-interactivity. */}
            <div
              className={`border-2 border-dashed bg-casevo-input text-casevo-forest p-6 rounded-lg text-center transition-colors ${
                mode === 'uploading' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              } ${isDragging ? 'border-casevo-mid' : 'border-casevo-border'}`}
              onClick={openFilePicker}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              role="button"
              aria-disabled={mode === 'uploading'}
            >
              {mode === 'uploading' ? (
                <p className="text-sm text-casevo-mid">Uploading {selectedFile?.name ?? ''}…</p>
              ) : mode === 'success' ? (
                <p className="text-sm text-casevo-brand">
                  Uploaded <span className="inline-block animate-pulse-once">✓</span>
                </p>
              ) : selectedFile ? (
                <div>
                  <p className="text-sm text-casevo-forest font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-casevo-mid mt-1">{formatBytes(selectedFile.size)}</p>
                  <p className="text-xs text-casevo-mid mt-2 italic">Click to choose a different file</p>
                </div>
              ) : (
                <p className="text-sm">Click to choose a file, or drag and drop here. Max 25&nbsp;MB.</p>
              )}
            </div>
            {/* Inline error display — validation errors above the
                upload errors so the user sees the most-recent failure
                last (mode==='error' is the result of an actual
                attempt; validation just rejected the queued file). */}
            {validationError && (
              <p className="text-sm text-casevo-danger">{validationError}</p>
            )}
            {mode === 'error' && errorMessage && (
              <p className="text-sm text-casevo-danger">{errorMessage}</p>
            )}

            {/* Footer — equal-width Cancel + Save pair (mirrors
                ClientPickerModal's create-form footer). Save is the
                only path that fires the upload; Cancel discards
                everything and closes. Both disabled mid-upload to
                prevent orphan-row races. Save also disabled when no
                file is queued or when validation has rejected the
                queued file. */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={mode === 'uploading'}
                className="flex-1 border border-casevo-border text-casevo-forest text-sm py-2 rounded hover:border-casevo-mid transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void handleSave() }}
                disabled={!selectedFile || mode === 'uploading' || validationError !== null}
                className="flex-1 bg-casevo-primary text-white text-sm py-2 rounded hover:bg-casevo-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mode === 'uploading' ? 'Uploading...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClientDocumentUploadModal
